import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Pencil, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SAVE_DEBOUNCE_MS = 1500;

/**
 * Single auto-saving reflection textarea. Saves 1.5s after typing stops via
 * PUT /api/mens-guide/answers.
 */
function ReflectionField({ moduleId, question, initialValue }) {
  const [value, setValue] = useState(initialValue || "");
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const timer = useRef(null);
  const lastSent = useRef(initialValue || "");

  const persist = useCallback(
    async (text) => {
      if (text === lastSent.current) return;
      setStatus("saving");
      try {
        await api.put("/mens-guide/answers", {
          module_id: moduleId,
          question_id: question.id,
          answer_text: text,
        });
        lastSent.current = text;
        setStatus("saved");
      } catch {
        setStatus("error");
      }
    },
    [moduleId, question.id],
  );

  const onChange = (e) => {
    const next = e.target.value;
    setValue(next);
    setStatus("idle");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(next), SAVE_DEBOUNCE_MS);
  };

  const onBlur = () => {
    if (timer.current) clearTimeout(timer.current);
    persist(value);
  };

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  return (
    <div className="bg-cream-soft border border-hairline rounded-2xl p-6 md:p-7">
      <p className="text-ink leading-relaxed">{question.text}</p>
      <textarea
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        rows={5}
        placeholder="Type here. Your answer saves automatically as you write."
        className="mt-5 w-full bg-cream border border-hairline rounded-xl px-4 py-3 text-ink placeholder-ink-soft/40 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 transition resize-y leading-relaxed"
        data-testid={`mg-reflection-${moduleId}-${question.id}`}
      />
      <div className="mt-2 h-5 flex items-center justify-end text-xs text-ink-soft">
        {status === "saving" && (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 size={12} strokeWidth={1.8} className="animate-spin" />
            Saving…
          </span>
        )}
        {status === "saved" && (
          <span className="inline-flex items-center gap-1.5 text-sage">
            <Check size={12} strokeWidth={2} /> Saved
          </span>
        )}
        {status === "error" && (
          <span className="text-red-600">Couldn't save — your connection may be flaky.</span>
        )}
      </div>
    </div>
  );
}

export default function MensGuideModuleView() {
  const { moduleId } = useParams();
  const [moduleData, setModuleData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [modules, setModules] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [moduleResp, answersResp, modulesResp] = await Promise.all([
          api.get(`/mens-guide/modules/${moduleId}`),
          api.get("/mens-guide/answers"),
          api.get("/mens-guide/modules"),
        ]);
        setModuleData(moduleResp.data);
        setAnswers(answersResp.data?.[moduleId] || {});
        setModules(modulesResp.data || []);
      } catch (err) {
        if (err?.response?.status === 403) {
          setError("locked");
        } else if (err?.response?.status === 404) {
          setError("notfound");
        } else {
          setError("generic");
        }
      }
    })();
  }, [moduleId]);

  if (error === "locked") {
    return (
      <ModuleMessage
        title="This module is part of the program."
        body="Unlock A Man's Guide to Her Menopause to access this teaching."
        ctaTo="/a-mans-guide-to-her-menopause"
        ctaLabel="Begin — $97"
      />
    );
  }
  if (error === "notfound") {
    return (
      <ModuleMessage
        title="Module not found."
        body="The module you're looking for doesn't exist."
        ctaTo="/dashboard/mens-guide"
        ctaLabel="Back to dashboard"
      />
    );
  }
  if (error === "generic") {
    return (
      <ModuleMessage
        title="Something went wrong."
        body="Could you refresh the page and try again?"
        ctaTo="/dashboard/mens-guide"
        ctaLabel="Back to dashboard"
      />
    );
  }
  if (!moduleData) return null;

  // Previous / next module navigation
  const currentIdx = modules.findIndex((m) => m.id === moduleData.id);
  const prevModule = currentIdx > 0 ? modules[currentIdx - 1] : null;
  const nextModule =
    currentIdx >= 0 && currentIdx < modules.length - 1
      ? modules[currentIdx + 1]
      : null;

  return (
    <div className="min-h-screen bg-cream" data-testid="page-mens-guide-module">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <Link
          to="/dashboard/mens-guide"
          className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink transition"
          data-testid="mg-module-back"
        >
          <ArrowLeft size={14} strokeWidth={1.6} /> All modules
        </Link>

        <div className="mt-8">
          <div className="eyebrow text-sage">Module {moduleData.number}</div>
          <h1
            className="mt-2 font-serif-display text-4xl md:text-5xl text-ink leading-[1.05]"
            data-testid="mg-module-title"
          >
            {moduleData.title}
          </h1>
          {moduleData.tagline && (
            <p className="mt-4 text-lg italic text-sage/80 leading-relaxed">
              {moduleData.tagline}
            </p>
          )}
        </div>

        {moduleData.video_url && (
          <div className="mt-10 md:mt-12 rounded-2xl overflow-hidden bg-black shadow-[0_30px_70px_-30px_rgba(74,93,79,0.45)]">
            <video
              src={moduleData.video_url}
              poster={moduleData.video_poster}
              controls
              playsInline
              preload="metadata"
              className="w-full block max-h-[80vh] mx-auto"
              data-testid="mg-module-video"
            />
          </div>
        )}

        {moduleData.summary && (
          <div className="mt-10 md:mt-12">
            <div className="bg-cream-soft border border-hairline rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-3">
                <BookOpen
                  size={18}
                  strokeWidth={1.6}
                  className="text-sage flex-shrink-0 mt-1"
                />
                <p className="text-ink-soft leading-relaxed">{moduleData.summary}</p>
              </div>
            </div>
          </div>
        )}

        {moduleData.reflection?.questions?.length > 0 && (
          <div className="mt-14 md:mt-20">
            <div className="flex items-center gap-3 mb-6">
              <Pencil size={18} strokeWidth={1.6} className="text-sage" />
              <h2 className="font-serif-display text-2xl md:text-3xl text-ink">
                Your reflections
              </h2>
            </div>
            {moduleData.reflection.intro && (
              <p
                className="text-ink-soft leading-relaxed mb-8 max-w-2xl"
                data-testid="mg-module-reflection-intro"
              >
                {moduleData.reflection.intro}
              </p>
            )}
            <div className="space-y-5">
              {moduleData.reflection.questions.map((q) => (
                <ReflectionField
                  key={q.id}
                  moduleId={moduleData.id}
                  question={q}
                  initialValue={answers[q.id] || ""}
                />
              ))}
            </div>
          </div>
        )}

        {moduleData.stay_action && (
          <div className="mt-14 md:mt-20">
            <div className="bg-sage text-cream rounded-3xl p-8 md:p-10">
              <div className="eyebrow text-gold mb-3">{moduleData.stay_action.title}</div>
              <p
                className="text-cream/90 leading-relaxed text-lg"
                data-testid="mg-module-stay-action"
              >
                {moduleData.stay_action.body}
              </p>
            </div>
          </div>
        )}

        <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-between border-t border-hairline pt-8">
          {prevModule ? (
            <Link
              to={`/dashboard/mens-guide/module/${prevModule.id}`}
              className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-sm transition"
              data-testid="mg-module-prev"
            >
              <ArrowLeft size={14} strokeWidth={1.6} />
              Previous · Module {prevModule.number}
            </Link>
          ) : (
            <span />
          )}
          {nextModule ? (
            <Link
              to={`/dashboard/mens-guide/module/${nextModule.id}`}
              className="inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium text-sm transition"
              data-testid="mg-module-next"
            >
              Next · Module {nextModule.number}
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          ) : (
            <Link
              to="/dashboard/mens-guide"
              className="inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium text-sm transition"
              data-testid="mg-module-finish"
            >
              Back to overview
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function ModuleMessage({ title, body, ctaTo, ctaLabel }) {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className="font-serif-display text-3xl text-ink">{title}</h1>
        <p className="mt-4 text-ink-soft leading-relaxed">{body}</p>
        <Link
          to={ctaTo}
          className="mt-8 inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-7 py-3.5 font-medium tracking-wide transition-all"
        >
          {ctaLabel}
          <ArrowRight size={15} strokeWidth={1.8} />
        </Link>
      </div>
    </div>
  );
}
